import React from 'react';
import { Shield, Cookie, Mail, Database, Eye, Phone } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-600 rounded-full mb-4">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-black text-black mb-4 font-inter uppercase">
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-600 font-inter">
            How we collect, use, and protect your personal information
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Introduction */}
        <div className="prose prose-lg max-w-none">
          <div className="bg-orange-50 border-l-4 border-orange-600 p-6 mb-8">
            <h2 className="text-2xl font-bold text-black mb-4">Our Commitment to Your Privacy</h2>
            <p className="text-gray-700">
              At Lawman Commercials, we are committed to protecting your privacy and personal data. 
              This Privacy Policy explains how we collect, use, store, and protect your information when you use our website, 
              services, or interact with us. We comply with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
            </p>
          </div>

          {/* Company Information */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-black mb-6 flex items-center gap-3">
              <Phone className="h-6 w-6 text-orange-600" />
              Who We Are
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p><strong>Company:</strong> Lawman Commercials</p>
              <p><strong>Address:</strong> 31 Merrylees Industrial Estate, Leeside, Desford, Leicester LE9 9FS</p>
              <p><strong>Phone:</strong> 01530 231 235</p>
              <p><strong>Email:</strong> technical@lawmancommercials.com</p>
              <p className="mt-3 text-sm text-gray-600">
                We are the data controller for the personal information you provide to us.
              </p>
            </div>
          </section>

          {/* Information We Collect */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-black mb-6 flex items-center gap-3">
              <Database className="h-6 w-6 text-orange-600" />
              Information We Collect
            </h2>
            
            <div className="space-y-6">
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-black mb-3">Information You Provide Directly</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li><strong>Contact Forms:</strong> Name, email address, phone number, company name, and your inquiry message</li>
                  <li><strong>Newsletter Subscription:</strong> Email address, name (optional), and marketing consent preferences</li>
                  <li><strong>Phone Inquiries:</strong> Information you provide during phone conversations about vehicle requirements</li>
                </ul>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-black mb-3">Information Collected Automatically</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li><strong>Website Usage:</strong> Pages visited, time spent on site, referring websites</li>
                  <li><strong>Device Information:</strong> IP address, browser type, device type, operating system</li>
                  <li><strong>Cookies:</strong> Technical cookies for website functionality (see Cookie Policy below)</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Information */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-black mb-6 flex items-center gap-3">
              <Eye className="h-6 w-6 text-orange-600" />
              How We Use Your Information
            </h2>
            
            <div className="grid gap-6">
              <div className="bg-orange-50 p-6 rounded-lg">
                <h3 className="text-lg font-bold text-black mb-3">Primary Business Purposes</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Respond to your inquiries about commercial vehicles</li>
                  <li>Provide information about our trucks, services, and pricing</li>
                  <li>Process vehicle hire requests and sales transactions</li>
                  <li>Maintain customer relationships and provide after-sales support</li>
                </ul>
              </div>

              <div className="bg-orange-50 p-6 rounded-lg">
                <h3 className="text-lg font-bold text-black mb-3">Marketing Communications (With Consent)</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Send newsletter updates about new vehicle arrivals</li>
                  <li>Share industry insights and market updates</li>
                  <li>Notify you about special offers and promotions</li>
                  <li>Provide information about vehicle hire opportunities</li>
                </ul>
              </div>

              <div className="bg-orange-50 p-6 rounded-lg">
                <h3 className="text-lg font-bold text-black mb-3">Website Improvement</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Analyse website usage to improve user experience</li>
                  <li>Identify technical issues and optimise performance</li>
                  <li>Understand customer preferences and interests</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Legal Basis */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-black mb-6">Legal Basis for Processing</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <ul className="space-y-3 text-gray-700">
                <li><strong>Legitimate Interest:</strong> Processing inquiries and providing customer service</li>
                <li><strong>Consent:</strong> Marketing communications and non-essential cookies</li>
                <li><strong>Contract Performance:</strong> Processing vehicle sales and hire agreements</li>
                <li><strong>Legal Obligation:</strong> Maintaining records as required by business regulations</li>
              </ul>
            </div>
          </section>

          {/* Cookie Policy */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-black mb-6 flex items-center gap-3">
              <Cookie className="h-6 w-6 text-orange-600" />
              Cookie Policy
            </h2>
            
            <div className="space-y-6">
              <p className="text-gray-700">
                We use cookies to enhance your browsing experience and analyse website performance. 
                You can manage your cookie preferences using the cookie consent banner that appears on your first visit.
              </p>

              <div className="grid gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-bold text-black mb-2">Necessary Cookies (Always Active)</h3>
                  <p className="text-sm text-gray-600">Essential for website functionality, security, and your consent preferences.</p>
                </div>
                
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-bold text-black mb-2">Analytics Cookies (Optional)</h3>
                  <p className="text-sm text-gray-600">Help us understand website usage patterns to improve user experience.</p>
                </div>
                
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-bold text-black mb-2">Marketing Cookies (Optional)</h3>
                  <p className="text-sm text-gray-600">Used to measure advertising effectiveness and personalise content.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Your Rights */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-black mb-6">Your Data Protection Rights</h2>
            
            <div className="bg-orange-50 border border-orange-200 p-6 rounded-lg">
              <p className="font-semibold text-black mb-4">Under UK GDPR, you have the following rights:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
                <li><strong>Rectification:</strong> Ask us to correct inaccurate or incomplete data</li>
                <li><strong>Erasure:</strong> Request deletion of your personal data (where legally possible)</li>
                <li><strong>Restriction:</strong> Ask us to limit how we use your data</li>
                <li><strong>Portability:</strong> Request your data in a portable format</li>
                <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Withdraw Consent:</strong> Unsubscribe from marketing communications at any time</li>
              </ul>
              
              <div className="mt-6 p-4 bg-white rounded border">
                <p className="text-sm">
                  <strong>To exercise your rights, contact us:</strong><br />
                  Email: technical@lawmancommercials.com<br />
                  Phone: 01530 231 235<br />
                  Post: 31 Merrylees Industrial Estate, Leeside, Desford, Leicester LE9 9FS
                </p>
              </div>
            </div>
          </section>

          {/* Data Security */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-black mb-6">Data Security & Retention</h2>
            
            <div className="grid gap-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-bold text-black mb-3">Security Measures</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>SSL encryption for data transmission</li>
                  <li>Secure database storage with access controls</li>
                  <li>Regular security updates and monitoring</li>
                  <li>Staff training on data protection principles</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-bold text-black mb-3">Data Retention</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li><strong>Contact Inquiries:</strong> 2 years from last contact</li>
                  <li><strong>Newsletter Subscriptions:</strong> Until you unsubscribe</li>
                  <li><strong>Customer Records:</strong> 7 years for business and tax purposes</li>
                  <li><strong>Website Analytics:</strong> 26 months (anonymized after 14 months)</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Third Parties */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-black mb-6">Sharing Your Information</h2>
            
            <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg">
              <p className="text-gray-700 mb-4">
                <strong>We do not sell, rent, or trade your personal information to third parties.</strong>
              </p>
              
              <p className="text-gray-700 mb-4">We may share your information only in these limited circumstances:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>Service Providers:</strong> Website hosting, email services (with appropriate data protection agreements)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our legitimate interests</li>
                <li><strong>Business Transfer:</strong> In the event of a merger, acquisition, or business transfer</li>
              </ul>
            </div>
          </section>

          {/* Contact & Complaints */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-black mb-6">Contact Us & Complaints</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-orange-50 p-6 rounded-lg">
                <h3 className="text-lg font-bold text-black mb-3">Data Protection Inquiries</h3>
                <p className="text-gray-700 mb-3">
                  For any questions about this Privacy Policy or your data rights:
                </p>
                <ul className="text-gray-700 space-y-1">
                  <li><strong>Email:</strong> technical@lawmancommercials.com</li>
                  <li><strong>Phone:</strong> 01530 231 235</li>
                  <li><strong>Post:</strong> Lawman Commercials, 31 Merrylees Industrial Estate, Leeside, Desford, Leicester LE9 9FS</li>
                </ul>
              </div>

              <div className="bg-orange-50 p-6 rounded-lg">
                <h3 className="text-lg font-bold text-black mb-3">File a Complaint</h3>
                <p className="text-gray-700 mb-3">
                  If you believe we've mishandled your personal data, you can file a complaint with:
                </p>
                <div className="text-gray-700">
                  <p className="font-semibold">Information Commissioner's Office (ICO)</p>
                  <p>Website: ico.org.uk</p>
                  <p>Phone: 0303 123 1113</p>
                </div>
              </div>
            </div>
          </section>

          {/* Policy Updates */}
          <section className="mb-12">
            <div className="bg-gray-100 p-6 rounded-lg border-l-4 border-orange-600">
              <h2 className="text-xl font-bold text-black mb-3">Policy Updates</h2>
              <p className="text-gray-700">
                We may update this Privacy Policy from time to time to reflect changes in our practices 
                or legal requirements. Any significant changes will be communicated via email to newsletter 
                subscribers or through a prominent notice on our website. The "Last updated" date at the 
                top of this policy indicates when the most recent changes were made.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;